import { usePageInfo } from 'app/frontend/base/config/queries'
import { createContext, useContext, useLayoutEffect, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  currentScheme: 'dark' | 'light'
}

const initialState: ThemeProviderState = {
  theme: 'dark',
  setTheme: () => null,
  currentScheme: 'dark',
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = 'dark',
  storageKey = 'wowaudit-theme',
  ...props
}: ThemeProviderProps) {
  // Can't use the useCustomDomain hook here because it starts as undefined
  const {
    data: { customDomain },
  } = usePageInfo()

  const customDomainTheme: Theme | null =
    customDomain?.themeMode &&
    ['dark', 'light'].includes(customDomain?.themeMode)
      ? (customDomain?.themeMode as Theme)
      : null

  const [theme, setTheme] = useState<Theme>(
    () =>
      customDomainTheme ||
      (localStorage.getItem(storageKey) as Theme) ||
      defaultTheme,
  )

  const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'

  useLayoutEffect(() => {
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')

    if (theme === 'system') {
      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
    currentScheme: theme === 'system' ? systemTheme : theme,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
